import { css } from '@emotion/react';
import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef } from 'react';

import { su, theme } from '@adflow/frontend/ui-theme';

const hubButtonBaseStyle = css(su.fontSans, su.roundedMd, {
	height: '2.5rem',
	/* @todo The shadow sticks when a button is clicked because it becomes focused. This looks odd. */
	'&:hover, &:focus': css(su.shadow, {
		outline: 'none',
	}),

	'&:not(:disabled, [data-disabled=true]):active': {
		/* @todo We would like the text of a button to not darken. */
		filter: 'brightness(81%)',
	},

	'&:disabled, &[data-disabled=true]': css({
		boxShadow: 'none',
		cursor: 'default',
	}),
});

type PaletteName = keyof typeof theme.colors.palettes;

const variants = {
	contained: (color: PaletteName) =>
		css({
			border: 'none',
			backgroundColor: theme.colors.palettes[color].main,
			color: theme.colors.palettes[color].contrastText,

			'&:disabled, &[data-disabled=true]': {
				backgroundColor: theme.colors.action.disabledBackground,
			},
		}),
	outlined: (color: PaletteName) =>
		css({
			border: '1px solid',
			borderColor: theme.colors.palettes[color].main,
			color: theme.colors.palettes[color].dark,
			backgroundColor: theme.colors.background.paper,

			'&:disabled, &[data-disabled=true]': {
				borderColor: theme.colors.action.disabledBackground,
				color: theme.colors.text.hint,
			},
		}),
};

const sizes = {
	regular: css({
		paddingLeft: '1rem',
		paddingRight: '1rem',
		minWidth: '5.75rem',
	}),
	small: css({
		paddingLeft: '0.5rem',
		paddingRight: '0.5rem',
		minWidth: '5.6rem',
	}),
	xsmall: css(su.textXs, {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingLeft: '0.5rem',
		paddingRight: '0.5rem',
		minWidth: '5rem',
		height: '1.75rem',
	}),
};

export interface HubButtonStyleProps {
	color?: PaletteName;
	variant?: keyof typeof variants;
	size?: keyof typeof sizes;
}

export const hubButtonStyle = ({
	color = 'primary',
	variant = 'outlined',
	size = 'regular',
}: HubButtonStyleProps) =>
	css(hubButtonBaseStyle, variants[variant](color), sizes[size]);

export interface ButtonProps
	extends
		HubButtonStyleProps,
		Omit<ComponentPropsWithoutRef<'button'>, keyof HubButtonStyleProps> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	function ButtonRender({ color, variant, size, ...buttonProps }, ref) {
		return (
			<button
				ref={ref}
				css={hubButtonStyle({ color, variant, size })}
				type="button"
				{...buttonProps}
			/>
		);
	}
);
